import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

interface User {
  id: number;
  email: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username: string, class_name: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      
      if (userId && token) {
        // 先设置用户状态（基于localStorage），避免立即重定向
        setUser({ id: parseInt(userId), email: '', isAdmin });
        
        // 然后验证token是否有效
        try {
          const response = await api.get('/user/profile');
          const userProfile = response.data;
          setUser({ 
            id: parseInt(userId), 
            email: userProfile.email || '', 
            isAdmin: userProfile.is_admin || isAdmin 
          });
          // 更新isAdmin状态
          if (userProfile.is_admin) {
            localStorage.setItem('isAdmin', 'true');
          } else {
            localStorage.removeItem('isAdmin');
          }
        } catch (error) {
          // Token无效，清除登录状态
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('isAdmin');
          setUser(null);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, userId, isAdmin } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userId.toString());
    if (isAdmin) {
      localStorage.setItem('isAdmin', 'true');
    }
    setUser({ id: userId, email, isAdmin: !!isAdmin });
  };

  const register = async (email: string, password: string, username: string, class_name: string) => {
    const response = await api.post('/auth/register', { email, password, username, class_name });
    await login(email, password);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('isAdmin');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isAdmin: !!user?.isAdmin,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

